import { brand } from "../../theme/businessFlowsTheme";
import { dev, mono } from "./devUi";

// Swagger UI ships a whole design of its own: its own font, its own greens and
// blues, hard shadows and square-ish boxes. Everything here is scoped to the
// one container the renderer lives in (nothing leaks to the rest of the app)
// and pulls it back to this product: our type scale, our navy and yellow, our
// radii, our alert styling, and the same dark material the guide's code blocks
// are made of. Its chrome - the topbar, the info block our guide already says
// better, and the Authorize box that only pretends to check a key - is hidden,
// so the key box above is the one way in.
const errorBg = "#fdeded";
const errorLine = "rgba(180, 35, 24, 0.35)";
const okFg = "#0b6b3a";

const swaggerSkin = {
  px: { xs: 0.5, sm: 1.5 },
  pb: 2,
  // The renderer's own chrome, replaced by the page's.
  "& .swagger-ui .topbar": { display: "none" },
  "& .swagger-ui .information-container": { display: "none" },
  "& .swagger-ui .scheme-container": { display: "none" },
  "& .swagger-ui .auth-wrapper": { display: "none" },
  // The padlock would open the renderer's own Authorize box, which only
  // remembers what was typed. The key box above is the one way in.
  "& .swagger-ui .authorization__btn": { display: "none" },
  "& .swagger-ui .wrapper": { padding: 0, maxWidth: "none" },
  "& .swagger-ui .opblock-tag-section": { marginBottom: "8px" },

  // Type: the app's font everywhere, ours for anything meant to be read as code.
  "& .swagger-ui, & .swagger-ui .opblock-tag, & .swagger-ui .opblock .opblock-summary-description, & .swagger-ui .btn, & .swagger-ui select, & .swagger-ui label, & .swagger-ui .tab li, & .swagger-ui table thead tr th":
    { fontFamily: "inherit !important", color: brand.ink },
  "& .swagger-ui .opblock-summary-path, & .swagger-ui .opblock-summary-path__deprecated, & .swagger-ui .prop-type, & .swagger-ui .prop-format, & .swagger-ui .parameter__type, & .swagger-ui .model, & .swagger-ui .model-title, & .swagger-ui code, & .swagger-ui pre, & .swagger-ui .microlight, & .swagger-ui .request-url, & .swagger-ui input[type=text], & .swagger-ui textarea":
    { fontFamily: `${mono} !important` },

  // Sections. The single untagged group the document has says nothing, so the
  // heading it would draw is dropped rather than shown reading "default".
  "& .swagger-ui .opblock-tag": {
    fontSize: "0.95rem",
    fontWeight: 700,
    letterSpacing: "-0.01em",
    borderBottom: `1px solid ${brand.line}`,
    padding: "10px 0",
    margin: "0 0 10px",
  },
  "& .swagger-ui .opblock-tag[data-tag='default']": { display: "none" },
  "& .swagger-ui .opblock-tag small": { color: brand.muted, fontSize: "0.82rem" },
  "& .swagger-ui .opblock-tag:focus-visible": { outline: `2px solid ${brand.ink}`, outlineOffset: "2px" },

  // One endpoint.
  "& .swagger-ui .opblock": {
    borderRadius: "14px",
    boxShadow: "none",
    border: `1px solid ${brand.line}`,
    background: "#fff",
    margin: "0 0 10px",
  },
  "& .swagger-ui .opblock.opblock-get": { background: "#fff", borderColor: brand.line },
  "& .swagger-ui .opblock.is-open": { borderColor: "#c9ccd4" },
  "& .swagger-ui .opblock .opblock-summary": { borderRadius: "14px", padding: "10px 12px", borderColor: "transparent", alignItems: "center" },
  "& .swagger-ui .opblock.is-open .opblock-summary": { borderRadius: "14px 14px 0 0", borderBottom: `1px solid ${brand.line}` },
  // The method chip, the same one the guide's endpoint list draws.
  "& .swagger-ui .opblock.opblock-get .opblock-summary-method, & .swagger-ui .opblock .opblock-summary-method": {
    background: brand.ink,
    color: "#fff",
    borderRadius: "6px",
    minWidth: "46px",
    fontFamily: `${mono} !important`,
    fontSize: "0.7rem",
    fontWeight: 700,
    letterSpacing: "0.06em",
    textShadow: "none",
    boxShadow: "none",
    padding: "5px 6px",
  },
  "& .swagger-ui .opblock .opblock-summary-path, & .swagger-ui .opblock-summary-path": {
    fontSize: "0.84rem",
    color: brand.ink,
    fontWeight: 600,
    maxWidth: "none",
  },
  "& .swagger-ui .opblock-summary-path a, & .swagger-ui .opblock-summary-path span": { color: brand.ink },
  "& .swagger-ui .opblock .opblock-summary-description": { color: brand.muted, fontSize: "0.82rem", lineHeight: 1.5 },
  "& .swagger-ui .opblock .opblock-section-header": {
    background: brand.canvas,
    boxShadow: "none",
    borderRadius: "10px",
    padding: "8px 14px",
    minHeight: 0,
  },
  "& .swagger-ui .opblock .opblock-section-header h4, & .swagger-ui .opblock-title_normal, & .swagger-ui .opblock-description-wrapper h4": {
    fontSize: "0.86rem",
    fontWeight: 700,
    color: brand.ink,
    letterSpacing: "-0.01em",
  },
  "& .swagger-ui .opblock-description-wrapper p, & .swagger-ui .opblock-external-docs-wrapper p": {
    color: brand.prose,
    fontSize: "0.9rem",
    lineHeight: 1.6,
  },

  // Buttons: contained navy for the one that acts, outlined for the rest.
  "& .swagger-ui .btn": { borderRadius: "10px", boxShadow: "none", fontWeight: 600, textTransform: "none", borderColor: brand.line },
  "& .swagger-ui .btn.execute": { background: brand.ink, borderColor: brand.ink, color: "#fff" },
  "& .swagger-ui .btn.execute:hover": { background: "#0f0f1d", borderColor: "#0f0f1d" },
  "& .swagger-ui .btn.try-out__btn": { background: "#fff", color: brand.ink, borderColor: "#cfd3da" },
  "& .swagger-ui .btn.cancel": { background: "#fff", color: "#b42318", borderColor: errorLine },
  "& .swagger-ui .btn:focus-visible, & .swagger-ui button:focus-visible, & .swagger-ui a:focus-visible, & .swagger-ui input:focus-visible, & .swagger-ui select:focus-visible":
    { outline: `2px solid ${brand.ink}`, outlineOffset: "2px" },

  // Fields, with the app's focus ring.
  "& .swagger-ui input[type=text], & .swagger-ui input[type=password], & .swagger-ui input[type=email], & .swagger-ui input[type=file], & .swagger-ui textarea, & .swagger-ui select":
    { borderRadius: "10px", border: "1px solid #cfd3da", background: "#fff", padding: "8px 10px", fontSize: "0.85rem", boxShadow: "none" },
  "& .swagger-ui input[type=text]:focus, & .swagger-ui textarea:focus, & .swagger-ui select:focus": {
    outline: "none",
    borderColor: brand.ink,
    boxShadow: "0 0 0 4px rgba(240, 230, 49, 0.55)",
  },
  "& .swagger-ui input.invalid, & .swagger-ui select.invalid, & .swagger-ui textarea.invalid": {
    background: errorBg,
    borderColor: errorLine,
    animation: "none",
  },

  // Refusals read like the app's alerts, not like a stack trace.
  "& .swagger-ui .errors-wrapper": {
    background: errorBg,
    border: `1px solid ${errorLine}`,
    borderLeft: "4px solid #b42318",
    borderRadius: "12px",
    padding: "14px 16px",
    margin: "16px 0",
    boxShadow: "none",
  },
  "& .swagger-ui .errors-wrapper .errors h4, & .swagger-ui .errors-wrapper hgroup h4": {
    color: "#b42318",
    fontSize: "0.95rem",
    fontWeight: 700,
    margin: 0,
    fontFamily: "inherit !important",
  },
  "& .swagger-ui .errors-wrapper .errors small, & .swagger-ui .errors-wrapper .error-wrapper .message": { color: "#7a2018", fontSize: "0.85rem" },
  "& .swagger-ui .errors-wrapper .btn": { background: "#fff", color: "#b42318", borderColor: errorLine },
  "& .swagger-ui .error-wrapper": { background: "transparent", boxShadow: "none" },
  "& .swagger-ui .validation-errors": { background: "transparent", color: "#7a2018" },

  // Tables of parameters and answers: hairlines, one weight for a heading, and
  // room to read a description beside its name.
  "& .swagger-ui table": { borderCollapse: "collapse" },
  "& .swagger-ui table thead tr th, & .swagger-ui table thead tr td": {
    color: brand.muted,
    fontSize: "0.74rem",
    fontWeight: 700,
    letterSpacing: "0.01em",
    borderBottom: `1px solid ${brand.line}`,
    padding: "8px 10px",
  },
  "& .swagger-ui table tbody tr td": { borderColor: brand.line, borderTop: `1px solid ${brand.line}`, padding: "12px 10px", verticalAlign: "top" },
  // The renderer bands alternate rows in grey; hairlines say the same thing
  // more quietly, and keep a parameter's name beside its description.
  "& .swagger-ui table tbody tr, & .swagger-ui table.parameters tbody tr": { background: "transparent" },
  "& .swagger-ui .parameters-col_name": { width: "36%" },
  "& .swagger-ui .parameters-col_name .parameter__name, & .swagger-ui td.parameters-col_name .parameter__name, & .swagger-ui .parameter__name": {
    color: brand.ink,
    fontWeight: 650,
    fontSize: "0.86rem",
    fontFamily: `${mono} !important`,
  },
  "& .swagger-ui .parameter__name.required": { fontWeight: 700 },
  "& .swagger-ui .parameter__name.required:after": { color: "#b42318", fontSize: "0.7rem", fontWeight: 600, padding: "0 0 0 4px" },
  "& .swagger-ui .parameter__type, & .swagger-ui .parameter__in": { color: brand.muted, fontSize: "0.74rem", fontStyle: "normal" },
  "& .swagger-ui .parameter__deprecated": { color: "#8a4b06" },
  "& .swagger-ui .parameter__extension": { display: "none" },
  "& .swagger-ui .parameters-col_description p, & .swagger-ui .markdown p": { color: brand.prose, fontSize: "0.86rem", lineHeight: 1.55, margin: 0 },

  // Status codes, coloured the way the guide colours them: green worked, red
  // change something, amber wait, slate ours.
  "& .swagger-ui .response-col_status": { color: brand.ink, fontWeight: 700, fontSize: "0.86rem", fontFamily: `${mono} !important` },
  "& .swagger-ui tr.response[data-code^='2'] .response-col_status": { color: okFg },
  "& .swagger-ui tr.response[data-code^='4'] .response-col_status": { color: "#a4271c" },
  "& .swagger-ui tr.response[data-code='402'] .response-col_status, & .swagger-ui tr.response[data-code='429'] .response-col_status": {
    color: "#8a4b06",
  },
  "& .swagger-ui tr.response[data-code^='5'] .response-col_status": { color: brand.muted },
  "& .swagger-ui .response-col_links": { color: brand.muted, fontSize: "0.8rem" },
  // The media-type picker arrives in the renderer's greens and reds.
  "& .swagger-ui .response-control-media-type--accept-controller select": { borderColor: "#cfd3da" },
  "& .swagger-ui .response-control-media-type__accept-message": { color: brand.muted, fontSize: "0.74rem" },
  "& .swagger-ui .response-control-media-type__accept-message code, & .swagger-ui .response-control-media-type__accept-message span": {
    color: brand.ink,
  },
  "& .swagger-ui .responses-inner": { padding: "12px 0 0" },
  "& .swagger-ui .response-control-media-type__title, & .swagger-ui .response-control-examples__title": {
    color: brand.muted,
    fontSize: "0.74rem",
    fontWeight: 700,
  },

  // The answer to a try-it call, made of the same dark material as the guide's
  // code blocks - including the curl line the renderer shows above it.
  "& .swagger-ui .highlight-code > .microlight, & .swagger-ui .curl-command .curl, & .swagger-ui .responses-inner pre": {
    background: `${dev.code} !important`,
    color: `${dev.codeText} !important`,
    borderRadius: "12px",
    fontSize: "0.78rem",
    lineHeight: 1.7,
    padding: "14px 16px",
  },
  "& .swagger-ui .highlight-code > .microlight span, & .swagger-ui .curl-command .curl span": { color: "inherit !important" },
  "& .swagger-ui .curl-command .copy-to-clipboard, & .swagger-ui .highlight-code .copy-to-clipboard": {
    background: "rgba(255,255,255,0.12)",
    borderRadius: "8px",
    border: "none",
    boxShadow: "none",
  },
  "& .swagger-ui .request-url": { fontSize: "0.78rem" },
  "& .swagger-ui .download-contents": { background: brand.ink, borderRadius: "8px", boxShadow: "none" },

  // Models.
  "& .swagger-ui section.models": { border: `1px solid ${brand.line}`, borderRadius: "14px", background: "#fff" },
  "& .swagger-ui section.models.is-open h4": { borderBottom: `1px solid ${brand.line}` },
  "& .swagger-ui section.models h4, & .swagger-ui .model-title": { color: brand.ink, fontWeight: 700, fontSize: "0.9rem" },
  "& .swagger-ui .model-box": { background: brand.canvas, borderRadius: "8px" },
  "& .swagger-ui .prop-type": { color: brand.muted },
  "& .swagger-ui .renderedMarkdown p": { color: brand.prose },
  "& .swagger-ui a": { color: brand.ink },
  "& .swagger-ui .tab li button.tablinks": { color: brand.muted, fontSize: "0.82rem" },
  "& .swagger-ui .tab li.active button.tablinks": { color: brand.ink, fontWeight: 700 },

  // Phone width: nothing runs off the side, and long paths wrap.
  "@media (max-width: 600px)": {
    "& .swagger-ui .opblock .opblock-summary": { flexWrap: "wrap", rowGap: "4px" },
    "& .swagger-ui .opblock-summary-path": { fontSize: "0.76rem", wordBreak: "break-all", maxWidth: "100%" },
    "& .swagger-ui .opblock .opblock-summary-description": { fontSize: "0.78rem", width: "100%", textAlign: "left" },
    "& .swagger-ui .opblock-body": { overflowX: "auto" },
    "& .swagger-ui .parameters-col_name": { width: "auto" },
    "& .swagger-ui .parameters-col_description input[type=text]": { width: "100%" },
    "& .swagger-ui .opblock .opblock-section-header": { flexWrap: "wrap", rowGap: "6px" },
    "& .swagger-ui .btn": { fontSize: "0.82rem" },
    "& .swagger-ui table tbody tr td": { padding: "10px 6px" },
  },
};

export default swaggerSkin;
